import TrackedLink from '@/components/tracking/TrackedLink'
import OutletIcon from '@/components/ui/OutletIcon'
import TopicAvatar from '@/components/ui/TopicAvatar'
import { getBiasCategory } from '@/utils/bias'
import Link from 'next/link'
import { Fragment } from 'react'
import { SIDE, WINGS } from './format'

// The storyline page's side column (the identity mocks, 2026-11-01): the
// most active outlets in three colour-topped groups, three per side, then
// the people and places its chapters name most, as pills.

const PER_SIDE = 3
const PILLS = 8

// Each outlet under its own side, by the outlet's rating (unrated reads as
// center), most articles first.
function groups(outlets) {
  const by = { left: [], center: [], right: [] }
  for (const o of outlets)
    by[typeof o.bias === 'number' ? getBiasCategory(o.bias) : 'center'].push(o)
  return WINGS.map((w) => [w, by[w].slice(0, PER_SIDE)]).filter(
    ([, list]) => list.length
  )
}

// The topics the chapters name, by how many chapters name each; ties go to
// the one in more articles, then by name.
function ranked(topics, days) {
  const byId = new Map(topics.map((t) => [t.id, t]))
  const counts = new Map()
  for (const d of days)
    for (const c of d.chapters)
      for (const id of c.topic_ids || []) {
        if (!byId.has(id)) continue
        const n = counts.get(id) || { chapters: 0, articles: 0 }
        n.chapters += 1
        n.articles += c.article_count || 0
        counts.set(id, n)
      }
  return [...counts.entries()]
    .map(([id, n]) => ({ ...byId.get(id), ...n }))
    .sort(
      (a, b) =>
        b.chapters - a.chapters ||
        b.articles - a.articles ||
        a.name.localeCompare(b.name)
    )
    .slice(0, PILLS)
}

export default function StorylineAside({
  outlets = [],
  topics = [],
  days = [],
  slug,
}) {
  const sides = groups(outlets)
  const pills = ranked(topics, days)
  if (!sides.length && !pills.length) return null

  return (
    <>
      {sides.length > 0 && (
        <>
          <h2 className="dateline mb-3.5">Most active outlets</h2>
          {sides.map(([w, list], i) => (
            <div
              key={w}
              className={`border-t-4 pt-2.5 ${SIDE[w].border} ${
                i === sides.length - 1 ? 'mb-7' : 'mb-[18px]'
              }`}
            >
              <p
                className={`mb-1.5 font-sans text-xs font-semibold uppercase tracking-[0.12em] ${SIDE[w].text}`}
              >
                {SIDE[w].label}
              </p>
              <p className="font-sans text-sm leading-[1.8] text-foreground">
                {list.map((o, j) => (
                  <Fragment key={o.slug}>
                    {j > 0 && <br />}
                    <TrackedLink
                      href={`/sources/${o.slug}`}
                      event="source_click"
                      params={{
                        source_surface: 'storyline_page',
                        source_slug: o.slug,
                        storyline_slug: slug,
                      }}
                      className="inline-flex items-center gap-2 hover:underline"
                    >
                      <OutletIcon outlet={o} size="sm" className="mx-0.5" />
                      {o.name}
                    </TrackedLink>
                    {' · '}
                    <span className="tabular-nums">{o.count}</span>
                  </Fragment>
                ))}
              </p>
            </div>
          ))}
        </>
      )}

      {pills.length > 0 && (
        <>
          <h2 className="dateline mb-3">People and places</h2>
          <div className="flex flex-wrap gap-2">
            {pills.map((t) => (
              <Link
                key={t.id}
                href={`/topic/${t.slug}`}
                className="inline-flex min-h-10 items-center gap-2 rounded-full border border-foreground pl-1.5 pr-3.5 font-sans text-sm text-foreground hover:underline"
              >
                <TopicAvatar topic={t} size="h-7 w-7 text-[10px]" />
                {t.name}
              </Link>
            ))}
          </div>
        </>
      )}
    </>
  )
}
